import { isCancelledOrderStatus, isOrderStatusWithPendingStock } from './orderStatus.js';

export const numeric = (v) => Number.isFinite(Number(v)) ? Number(v) : 0;
export const sum = (rows, field) => rows.reduce((n, row) => n + numeric(typeof field === 'function' ? field(row) : row[field]), 0);
export const clean = (v) => String(v ?? '').trim();
export const day = (v) => {
  if (!v) return '';
  const value = String(v);
  if (value.length <= 10) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : madridToday(parsed);
};
export const inPeriod = (date, filters) => Boolean((!filters.from || day(date) >= filters.from) && (!filters.to || (day(date) && day(date) <= filters.to)));
export const seasonMatches = (season, filters) => !filters.season || clean(season) === filters.season;
export const pendingAmount = (r) => Math.max(0, numeric(r.total_amount) - numeric(r.paid_amount));
export const eligibleInvoice = (r) => ['confirmed', 'partial', 'paid'].includes(r.status);
export const madridToday = (date = new Date()) => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Madrid' }).format(date);

export function groupRows(rows, key, value) {
  const groups = new Map();
  for (const row of rows) {
    const id = key(row) || 'Sin asignar';
    groups.set(id, (groups.get(id) || 0) + numeric(value(row)));
  }
  return [...groups].map(([id, value]) => ({ id, label: id, value }));
}

// Keep cancelled remainders separate from served quantities, including legacy archives.
export function commercialRows(lines, cancelledLines) {
  const archived = new Set(cancelledLines.map((r) => r.original_order_item_id).filter(Boolean));
  const live = new Set(lines.map((r) => r.id));
  const rows = [];
  const add = (r, kind, quantity, archive = false) => {
    if (!quantity) return;
    rows.push({ ...r, id: `${archive ? 'archive' : 'live'}:${r.id}:${kind}`, kind, quantity,
      amount: quantity * numeric(r.price), date: r.order?.date, season: clean(r.product?.temporada),
      label: `${r.order?.number || 'Sin número'} · ${r.product?.code || 'Sin artículo'}`, detail: `${r.size || ''} / ${r.color || ''}` });
  };
  for (const r of lines) {
    const cancelled = isCancelledOrderStatus(r.order?.status);
    const served = numeric(r.delivered_quantity ?? (r.order?.status === 'completed' ? r.quantity : 0));
    add(r, 'served', served);
    add(r, 'pending', isOrderStatusWithPendingStock(r.order?.status) ? numeric(r.pending_quantity) : 0);
    if (cancelled && !archived.has(r.id)) add(r, 'cancelled', Math.max(0, numeric(r.quantity) - served));
  }
  for (const r of cancelledLines) {
    add(r, 'cancelled', numeric(r.pending_quantity ?? r.quantity), true);
    if (!live.has(r.original_order_item_id)) add(r, 'served', numeric(r.delivered_quantity), true);
  }
  return rows;
}

export function agingBucket(invoice, today) {
  if (!invoice.due_date) return 'Sin vencimiento';
  const days = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${day(invoice.due_date)}T00:00:00Z`)) / 86400000);
  if (days <= 0) return 'No vencido';
  if (days <= 30) return '1–30 días';
  if (days <= 60) return '31–60 días';
  if (days <= 90) return '61–90 días';
  return '>90 días';
}

export function flattenProduction(orders) {
  return orders.flatMap((order) => (order.products || []).map((r) => ({ ...r,
    order_id: order.id, order_number: order.number, season: clean(order.temporada),
    family: clean(order.cod_fam), delivery_date: order.delivery_date,
    label: `${r.subnumber} · ${r.product_code_snapshot || ''}`, date: r.order_date,
  })));
}

export function productionMatches(row, status, today) {
  if (!status) return true;
  if (status === 'manufactured') return row.manufacturing_complete === true;
  if (status === 'in_progress') return row.manufacturing_complete !== true;
  if (status === 'late') return !row.manufacturing_complete && row.delivery_date && day(row.delivery_date) < today;
  if (status === 'incident') return row.has_incident === true;
  if (status === 'invoices') return numeric(row.pending_invoice_count) > 0;
  if (status === 'documents') return numeric(row.pending_document_count) > 0;
  return row.status === status;
}

export function materialStatus(row) {
  if (row.confirmed_quantity == null) return 'Pendiente';
  if (numeric(row.confirmed_quantity) === 0) return 'Sin disponibilidad';
  return numeric(row.confirmed_quantity) < numeric(row.quantity) ? 'Parcial' : 'Confirmado';
}

export function stockRows(rows) {
  return rows.map((r) => ({ ...r, label: r.product?.code || 'Sin artículo',
    detail: `${r.size || ''} / ${r.color || ''}`, season: clean(r.product?.temporada),
    available: numeric(r.quantity) - numeric(r.pending_orders),
    missing: Math.max(0, numeric(r.pending_orders) - numeric(r.quantity)),
    amount: r.product?.price == null ? null : numeric(r.quantity) * numeric(r.product.price),
  }));
}
